import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Package,
  Calendar,
  Receipt,
  Truck,
  Clock,
  Wallet,
  Download,
  Printer,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  PackageCheck,
  Boxes,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from '../components/PharmacyLogo';
import { formatPakistanDate, formatPakistanDateTime, pakistanToday } from '../utils/pakistanTime';

const tabs = [
  { id: 'daily', label: 'Daily Report', icon: Calendar },
  { id: 'profit', label: 'Profit Report', icon: TrendingUp },
  { id: 'stock', label: 'Stock Report', icon: Package },
  { id: 'sales', label: 'Sales Report', icon: ShoppingCart },
  { id: 'purchases', label: 'Purchase Report', icon: Truck },
  { id: 'expenses', label: 'Expense Report', icon: Receipt },
  { id: 'item_sales', label: 'Item Sales Report', icon: PackageCheck },
];

const getTodayStr = () => pakistanToday();

function formatItemQuantityDisplay(baseQty, units = []) {
  const qty = Number(baseQty) || 0;
  if (!units || units.length <= 1) {
    const unitName = units?.[0]?.unit_name || 'units';
    return {
      primary: `${qty} ${unitName}`,
      breakdown: null,
      fullText: `${qty} ${unitName}`,
      baseUnit: unitName,
    };
  }

  const topUnit = units[0];
  const baseUnit = units[units.length - 1];
  const topFactor = Number(topUnit.conversion_factor) || 1;
  const baseName = baseUnit?.unit_name || 'units';
  const topName = topUnit?.unit_name || 'Box';

  if (topFactor <= 1) {
    return {
      primary: `${qty} ${baseName}`,
      breakdown: null,
      fullText: `${qty} ${baseName}`,
      baseUnit: baseName,
    };
  }

  const fullBoxes = Math.floor(qty / topFactor);
  const remainingBase = qty % topFactor;

  let breakdownStr = '';
  if (remainingBase === 0) {
    breakdownStr = `${fullBoxes} ${topName}${fullBoxes !== 1 ? 's' : ''}`;
  } else if (fullBoxes === 0) {
    breakdownStr = `${remainingBase} ${baseName}${remainingBase !== 1 ? 's' : ''}`;
  } else {
    breakdownStr = `${fullBoxes} ${topName}${fullBoxes !== 1 ? 's' : ''} + ${remainingBase} ${baseName}${remainingBase !== 1 ? 's' : ''}`;
  }

  return {
    primary: `${qty} ${baseName}${qty !== 1 ? 's' : ''}`,
    breakdown: breakdownStr,
    fullText: `${qty} ${baseName}${qty !== 1 ? 's' : ''} (${breakdownStr})`,
    baseUnit: baseName,
  };
}

export default function Reports() {
  const { settings } = useSettings?.() || {};
  const pharmacyName = settings?.pharmacy_name || 'Gaza Pharmacy';
  const [activeTab, setActiveTab] = useState('daily');
  const [showExportPDF, setShowExportPDF] = useState(false);
  const today = getTodayStr();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  // Financial report data (Daily, Profit, Sales, Purchases, Expenses)
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);

  // Stock report data
  const [stockData, setStockData] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockStatus, setStockStatus] = useState('in_stock');
  const [stockSearch, setStockSearch] = useState('');
  const [stockPage, setStockPage] = useState(1);
  const [exportMedicines, setExportMedicines] = useState([]);

  // Sold Items report data
  const [soldItemsData, setSoldItemsData] = useState(null);
  const [soldItemsLoading, setSoldItemsLoading] = useState(false);
  const [soldItemsSearch, setSoldItemsSearch] = useState('');
  const [soldItemsPage, setSoldItemsPage] = useState(1);
  const [exportSoldItems, setExportSoldItems] = useState([]);

  useEffect(() => {
    loadFinancialData(dateFrom, dateTo);
    loadStockData(1, stockStatus, stockSearch);
    loadSoldItemsData(1, soldItemsSearch, dateFrom, dateTo);
  }, []);

  const loadFinancialData = async (from = dateFrom, to = dateTo) => {
    setFinancialLoading(true);
    try {
      const res = await api.get(`/reports/financial?date_from=${from}&date_to=${to}`);
      setFinancialData(res);
    } catch (err) {
      toast.error('Failed to load financial report data');
    } finally {
      setFinancialLoading(false);
    }
  };

  const loadStockData = async (targetPage = stockPage, targetStatus = stockStatus, targetSearch = stockSearch) => {
    setStockLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: '50',
        status: targetStatus,
      });
      if (targetSearch.trim()) {
        params.append('search', targetSearch.trim());
      }
      const res = await api.get(`/reports/stock?${params.toString()}`);
      setStockData(res);
    } catch (err) {
      toast.error('Failed to load stock report data');
    } finally {
      setStockLoading(false);
    }
  };

  const loadSoldItemsData = async (targetPage = soldItemsPage, targetSearch = soldItemsSearch, from = dateFrom, to = dateTo) => {
    setSoldItemsLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: from,
        date_to: to,
        page: String(targetPage),
        limit: '8',
      });
      if (targetSearch.trim()) {
        params.append('search', targetSearch.trim());
      }
      const res = await api.get(`/reports/sold-items?${params.toString()}`);
      setSoldItemsData(res);
    } catch (err) {
      toast.error('Failed to load item sales report data');
    } finally {
      setSoldItemsLoading(false);
    }
  };

  const handleStockStatusChange = (newStatus) => {
    setStockStatus(newStatus);
    setStockPage(1);
    loadStockData(1, newStatus, stockSearch);
  };

  const handleStockSearch = (e) => {
    e.preventDefault();
    setStockPage(1);
    loadStockData(1, stockStatus, stockSearch);
  };

  const handleStockPageChange = (newPage) => {
    setStockPage(newPage);
    loadStockData(newPage, stockStatus, stockSearch);
  };

  const handleSoldItemsSearchSubmit = (e) => {
    e.preventDefault();
    setSoldItemsPage(1);
    loadSoldItemsData(1, soldItemsSearch, dateFrom, dateTo);
  };

  const handleApplyDates = (from = dateFrom, to = dateTo) => {
    loadFinancialData(from, to);
    setSoldItemsPage(1);
    loadSoldItemsData(1, soldItemsSearch, from, to);
  };

  const handleOpenExport = async () => {
    setShowExportPDF(true);
    if (activeTab === 'stock') {
      try {
        const res = await api.get('/reports/stock?export=1&status=in_stock');
        setExportMedicines(res?.items || []);
      } catch (err) {
        console.warn('Failed to load export stock items:', err);
      }
    } else if (activeTab === 'item_sales') {
      try {
        const params = new URLSearchParams({
          date_from: dateFrom,
          date_to: dateTo,
          export: '1',
        });
        if (soldItemsSearch.trim()) {
          params.append('search', soldItemsSearch.trim());
        }
        const res = await api.get(`/reports/sold-items?${params.toString()}`);
        setExportSoldItems(res?.items || []);
      } catch (err) {
        console.warn('Failed to load export sold items:', err);
      }
    }
  };

  const resetDate = () => {
    setDateFrom('');
    setDateTo('');
    loadFinancialData('', '');
    setSoldItemsPage(1);
    loadSoldItemsData(1, soldItemsSearch, '', '');
  };

  // Financial aggregates
  const sales = financialData?.sales?.items || [];
  const totalSales = financialData?.sales?.total || 0;
  const paidSales = financialData?.sales?.paid || 0;
  const creditSales = financialData?.sales?.credit || 0;
  const salesCount = financialData?.sales?.count || 0;

  const purchases = financialData?.purchases?.items || [];
  const totalPurchases = financialData?.purchases?.total || 0;
  const purchasesCount = financialData?.purchases?.count || 0;

  const expenses = financialData?.expenses?.items || [];
  const totalExpenses = financialData?.expenses?.total || 0;
  const expensesCount = financialData?.expenses?.count || 0;
  const expensesCategories = financialData?.expenses?.categories || [];

  const suppliersCount = financialData?.suppliers_count || 0;

  // Purchase spend and cost of goods sold are intentionally different: stock
  // may be bought in one period and sold in another. The API provides COGS
  // from the sold items' recorded cost for a reliable profit breakdown.
  const cogs = financialData?.profit?.cogs ?? 0;
  const grossProfit = financialData?.profit?.grossProfit ?? (totalSales - cogs);
  const profit = financialData?.profit?.netProfit ?? (grossProfit - totalExpenses);
  const grossMargin = financialData?.profit?.grossMargin ?? (totalSales > 0 ? ((grossProfit / totalSales) * 100) : 0);
  const netMargin = financialData?.profit?.netMargin ?? (totalSales > 0 ? ((profit / totalSales) * 100) : 0);

  // Stock aggregates - perfectly aligned with Dashboard & Accounting
  const stockSummary = stockData?.summary || {
    stockValue: 0,
    retailValue: 0,
    potentialProfit: 0,
    margin: 0,
    inStockItems: 0,
    totalMedicines: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  };
  const stockItems = stockData?.items || [];
  const stockPagination = stockData?.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 };

  // Sold Items aggregates
  const soldItemsSummary = soldItemsData?.summary || {
    totalSoldQty: 0,
    totalSoldAmount: 0,
    totalSoldCost: 0,
    totalProfit: 0,
    margin: 0,
    distinctItemsCount: 0,
    totalTransactions: 0,
  };
  const soldItemsList = soldItemsData?.items || [];
  const soldItemsPagination = soldItemsData?.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 };

  const getTabLabel = (id) => {
    switch (id) {
      case 'daily': return 'Daily Business Report';
      case 'profit': return 'Profit & Loss Statement';
      case 'stock': return 'Inventory & Stock Report';
      case 'sales': return 'Sales History Report';
      case 'purchases': return 'Purchase Orders Report';
      case 'expenses': return 'Operating Expenses Report';
      case 'item_sales': return 'Item Sales & Stock Movement Report';
      default: return 'Business Report';
    }
  };

  const renderPrintableReport = () => {
    const printableMedicines = exportMedicines.length > 0 ? exportMedicines : stockItems;
    const printableSoldItems = exportSoldItems.length > 0 ? exportSoldItems : soldItemsList;

    return (
      <div style={{ width: '100%', maxWidth: 750, background: '#ffffff', padding: 24, margin: '0 auto', color: '#1a1a1a', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        {/* REPORT HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, borderBottom: '3px solid #22c55e', paddingBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <PharmacyLogo logo={settings?.pharmacy_logo} alt={`${pharmacyName} logo`} className="h-12 w-12 shrink-0 rounded-lg" imageClassName="h-12 w-12 shrink-0 rounded-lg object-contain" />
            <div>
              <h1 style={{ fontSize: 22, color: '#22c55e', margin: 0, fontWeight: 700 }}>{pharmacyName}</h1>
              <p style={{ fontSize: 14, color: '#374151', margin: '3px 0 0', fontWeight: 600 }}>{getTabLabel(activeTab)}</p>
              {settings?.pharmacy_address && (
                <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>
              )}
              {settings?.pharmacy_phone && (
                <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>Generated: {formatPakistanDateTime(new Date())}</p>
            {activeTab !== 'stock' ? (
              <p style={{ fontSize: 11, color: '#059669', fontWeight: 600, margin: '3px 0 0' }}>
                Period: {dateFrom || 'Start'} to {dateTo || 'End'}
              </p>
            ) : (
              <p style={{ fontSize: 11, color: '#059669', fontWeight: 600, margin: '3px 0 0' }}>
                As of: {formatPakistanDate(new Date())}
              </p>
            )}
          </div>
        </div>

        {/* TAB 1: DAILY */}
        {activeTab === 'daily' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Sales</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#2563eb', marginTop: 3, marginBottom: 0 }}>Rs. {totalSales.toFixed(2)}</p>
                <p style={{ fontSize: 9, color: '#9ca3af', margin: '2px 0 0' }}>{salesCount} transactions</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Purchases</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#7c3aed', marginTop: 3, marginBottom: 0 }}>Rs. {totalPurchases.toFixed(2)}</p>
                <p style={{ fontSize: 9, color: '#9ca3af', margin: '2px 0 0' }}>{purchasesCount} orders</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Expenses</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', marginTop: 3, marginBottom: 0 }}>Rs. {totalExpenses.toFixed(2)}</p>
                <p style={{ fontSize: 9, color: '#9ca3af', margin: '2px 0 0' }}>{expensesCount} entries</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: profit >= 0 ? '#f0fdf4' : '#fef2f2' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: profit >= 0 ? '#166534' : '#991b1b', margin: 0 }}>Net Profit</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: profit >= 0 ? '#16a34a' : '#dc2626', marginTop: 3, marginBottom: 0 }}>Rs. {profit.toFixed(2)}</p>
                <p style={{ fontSize: 9, color: '#9ca3af', margin: '2px 0 0' }}>{netMargin}% margin</p>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#374151', marginBottom: 8 }}>Recent Sales ({sales.length} of {salesCount})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Invoice</th>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Date / Time</th>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>No sales in this period</td></tr>
                  ) : (
                    sales.slice(0, 15).map((s, i) => (
                      <tr key={s.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{s.invoice_number}</td>
                        <td style={{ padding: '6px 8px', color: '#4b5563' }}>{formatPakistanDateTime(s.created_at)}</td>
                        <td style={{ padding: '6px 8px', textTransform: 'capitalize', color: s.payment_status === 'paid' ? '#16a34a' : '#d97706', fontWeight: 600 }}>{s.payment_status}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Rs. {(parseFloat(s.total) || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                  <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                    <td colSpan={3} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total Sales:</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#15803d', fontSize: 11 }}>Rs. {totalSales.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#374151', marginBottom: 8 }}>Expenses Breakdown ({expensesCategories.length} categories)</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Category / Type</th>
                    <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {expensesCategories.length === 0 ? (
                    <tr><td colSpan={2} style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>No expenses in this period</td></tr>
                  ) : (
                    expensesCategories.map((cat, i) => (
                      <tr key={cat.category || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 500 }}>{cat.category}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {(parseFloat(cat.total) || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                  <tr style={{ borderTop: '2px solid #22c55e', background: '#fef2f2' }}>
                    <td style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#991b1b', textTransform: 'uppercase', fontSize: 10 }}>Total Expenses:</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#dc2626', fontSize: 11 }}>Rs. {totalExpenses.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: PROFIT */}
        {activeTab === 'profit' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Revenue</p>
                <p style={{ fontSize: 15, fontWeight: 700, marginTop: 4, marginBottom: 0 }}>Rs. {totalSales.toFixed(2)}</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Cost of Goods</p>
                <p style={{ fontSize: 15, fontWeight: 700, marginTop: 4, marginBottom: 0 }}>Rs. {cogs.toFixed(2)}</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Expenses</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', marginTop: 4, marginBottom: 0 }}>Rs. {totalExpenses.toFixed(2)}</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: profit >= 0 ? '#f0fdf4' : '#fef2f2' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: profit >= 0 ? '#166534' : '#991b1b', margin: 0 }}>Net Profit</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: profit >= 0 ? '#16a34a' : '#dc2626', marginTop: 4, marginBottom: 0 }}>Rs. {profit.toFixed(2)}</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 20 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Financial Metric</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Amount / Ratio</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>Cash Sales (Paid in full)</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>Rs. {paidSales.toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>Credit / Outstanding Sales</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#d97706' }}>Rs. {creditSales.toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>Gross Profit (Revenue - Cost of Goods)</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>Rs. {grossProfit.toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>Gross Margin</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{grossMargin}%</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>Total Operating Expenses</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {totalExpenses.toFixed(2)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td style={{ padding: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#166534', fontSize: 11 }}>Net Profit / (Loss)</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 800, color: profit >= 0 ? '#16a34a' : '#dc2626', fontSize: 13 }}>Rs. {profit.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 3: STOCK */}
        {activeTab === 'stock' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f0fdf4' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#166534', margin: 0 }}>Stock Value (Cost)</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#16a34a', marginTop: 3, marginBottom: 0 }}>Rs. {stockSummary.stockValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>Cost price inventory</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Retail Value</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#2563eb', marginTop: 3, marginBottom: 0 }}>Rs. {stockSummary.retailValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>Selling price inventory</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>In-Stock Items</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginTop: 3, marginBottom: 0 }}>{stockSummary.inStockItems}</p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>of {stockSummary.totalMedicines} total</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Low Stock / Out</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#d97706', marginTop: 3, marginBottom: 0 }}>{stockSummary.lowStockCount} / {stockSummary.outOfStockCount}</p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>Alerts</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Medicine Name</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Stock</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Cost Price</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Selling Price</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Stock Value (Cost)</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {printableMedicines.map((m, i) => (
                  <tr key={m.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                      {m.name}
                      {m.generic_name && <span style={{ display: 'block', fontSize: 9, color: '#6b7280', fontWeight: 400 }}>{m.generic_name}</span>}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600 }}>
                      <div>{formatItemQuantityDisplay(m.total_stock, m.units).primary}</div>
                      {formatItemQuantityDisplay(m.total_stock, m.units).breakdown && (
                        <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 400 }}>
                          {formatItemQuantityDisplay(m.total_stock, m.units).breakdown}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>Rs. {(parseFloat(m.cost_price) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>Rs. {(parseFloat(m.selling_price) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>Rs. {(parseFloat(m.cost_value) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, fontWeight: 600, color: m.status === 'out_of_stock' ? '#dc2626' : m.status === 'low_stock' ? '#d97706' : '#16a34a' }}>
                      {m.status === 'out_of_stock' ? 'Out of Stock' : m.status === 'low_stock' ? 'Low Stock' : 'In Stock'}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td colSpan={5} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total Stock Value (Cost):</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>Rs. {stockSummary.stockValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 4: SALES */}
        {activeTab === 'sales' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Sales</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#2563eb', marginTop: 4, marginBottom: 0 }}>Rs. {totalSales.toFixed(2)}</p>
                <p style={{ fontSize: 10, color: '#9ca3af', margin: '2px 0 0' }}>{salesCount} invoices</p>
              </div>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Paid Amount</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#16a34a', marginTop: 4, marginBottom: 0 }}>Rs. {paidSales.toFixed(2)}</p>
              </div>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Credit / Outstanding</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#d97706', marginTop: 4, marginBottom: 0 }}>Rs. {creditSales.toFixed(2)}</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Invoice</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Total</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Paid</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s, i) => (
                  <tr key={s.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{s.invoice_number}</td>
                    <td style={{ padding: '6px 8px', color: '#4b5563' }}>{formatPakistanDate(s.created_at)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Rs. {(parseFloat(s.total) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>Rs. {(parseFloat(s.paid_amount) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', textTransform: 'capitalize', fontWeight: 600, color: s.payment_status === 'paid' ? '#16a34a' : '#d97706' }}>{s.payment_status}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td colSpan={3} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total:</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>Rs. {totalSales.toFixed(2)}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>Rs. {paidSales.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 5: PURCHASES */}
        {activeTab === 'purchases' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Purchases</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', marginTop: 4, marginBottom: 0 }}>Rs. {totalPurchases.toFixed(2)}</p>
                <p style={{ fontSize: 10, color: '#9ca3af', margin: '2px 0 0' }}>{purchasesCount} orders</p>
              </div>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Active Suppliers</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#16a34a', marginTop: 4, marginBottom: 0 }}>{suppliersCount}</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Reference</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Total Amount</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p, i) => (
                  <tr key={p.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{p.reference_number}</td>
                    <td style={{ padding: '6px 8px', color: '#4b5563' }}>{new Date(p.purchase_date || p.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Rs. {(parseFloat(p.total) || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', textTransform: 'capitalize', fontWeight: 600, color: p.payment_status === 'paid' ? '#16a34a' : '#d97706' }}>{p.payment_status}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td colSpan={3} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total Purchases:</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#7c3aed', fontSize: 11 }}>Rs. {totalPurchases.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 6: EXPENSES */}
        {activeTab === 'expenses' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Expenses</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', marginTop: 4, marginBottom: 0 }}>Rs. {totalExpenses.toFixed(2)}</p>
              </div>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Categories</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#16a34a', marginTop: 4, marginBottom: 0 }}>{expensesCategories.length}</p>
              </div>
              <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Entries</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: '#1f2937', marginTop: 4, marginBottom: 0 }}>{expensesCount}</p>
              </div>
            </div>

            {expensesCategories.length > 1 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#4b5563', marginBottom: 8 }}>Category Breakdown</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                  {expensesCategories.map((cat, i) => (
                    <div key={cat.category || i} style={{ padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 500 }}>{cat.category}</span>
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Rs. {(parseFloat(cat.total) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Category / Type</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Details</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={e.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{e.category}</td>
                    <td style={{ padding: '6px 8px', color: '#4b5563' }}>{e.description || '-'}</td>
                    <td style={{ padding: '6px 8px', color: '#4b5563' }}>{e.date}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {(parseFloat(e.amount) || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#fef2f2' }}>
                  <td colSpan={4} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#991b1b', textTransform: 'uppercase', fontSize: 10 }}>Total Expenses:</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#dc2626', fontSize: 11 }}>Rs. {totalExpenses.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 7: ITEM SALES */}
        {activeTab === 'item_sales' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f0fdf4' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#166534', margin: 0 }}>Total Units Sold</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#16a34a', marginTop: 3, marginBottom: 0 }}>
                  {(soldItemsSummary.totalSoldQty || 0).toLocaleString()}
                </p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>{soldItemsSummary.totalTransactions || 0} transactions</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Sales Value</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#2563eb', marginTop: 3, marginBottom: 0 }}>
                  Rs. {(soldItemsSummary.totalSoldAmount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                </p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>Revenue generated</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Distinct Items</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginTop: 3, marginBottom: 0 }}>
                  {soldItemsSummary.distinctItemsCount || 0}
                </p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>Products sold</p>
              </div>
              <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Estimated Profit</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#059669', marginTop: 3, marginBottom: 0 }}>
                  Rs. {(soldItemsSummary.totalProfit || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                </p>
                <p style={{ fontSize: 9, color: '#6b7280', margin: '2px 0 0' }}>{soldItemsSummary.margin || 0}% margin</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Medicine Name</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Category</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Sold Qty</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Remaining</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'center', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Initial Qty</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Avg Price</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '6px 8px', fontSize: 9, textTransform: 'uppercase' }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {printableSoldItems.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>No sold items in selected period</td></tr>
                ) : (
                  printableSoldItems.map((item, i) => (
                    <tr key={item.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                        {item.name}
                        {item.generic_name && <span style={{ display: 'block', fontSize: 9, color: '#6b7280', fontWeight: 400 }}>{item.generic_name}</span>}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#4b5563', fontSize: 10 }}>{item.category_name}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>
                        <div>{formatItemQuantityDisplay(item.sold_qty, item.units).primary}</div>
                        {formatItemQuantityDisplay(item.sold_qty, item.units).breakdown && (
                          <div style={{ fontSize: 8, color: '#15803d', fontWeight: 500 }}>
                            {formatItemQuantityDisplay(item.sold_qty, item.units).breakdown}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: item.remaining_stock <= 0 ? '#dc2626' : '#374151' }}>
                        <div>{formatItemQuantityDisplay(item.remaining_stock, item.units).primary}</div>
                        {formatItemQuantityDisplay(item.remaining_stock, item.units).breakdown && (
                          <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 400 }}>
                            {formatItemQuantityDisplay(item.remaining_stock, item.units).breakdown}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>
                        <div>{formatItemQuantityDisplay(item.initial_stock, item.units).primary}</div>
                        {formatItemQuantityDisplay(item.initial_stock, item.units).breakdown && (
                          <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 400 }}>
                            {formatItemQuantityDisplay(item.initial_stock, item.units).breakdown}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <div>Rs. {(parseFloat(item.avg_price) || 0).toFixed(2)}</div>
                        <div style={{ fontSize: 8, color: '#9ca3af' }}>/ {item.base_unit || item.unit || 'unit'}</div>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>
                        Rs. {(parseFloat(item.total_amount) || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td colSpan={3} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Grand Total:</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>
                    {soldItemsSummary.totalSoldQty || 0}
                  </td>
                  <td colSpan={3}></td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>
                    Rs. {(soldItemsSummary.totalSoldAmount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* FOOTER */}
        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12, lineHeight: 1.6 }}>
          <div>{pharmacyName} Management System — Printed on {formatPakistanDate(new Date(), { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div style={{ marginTop: 2, color: '#6b7280' }}>
            Developed by{' '}
            <a
              href="https://jahasoft.pk"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#059669', textDecoration: 'none', fontWeight: 600 }}
            >
              Jahasoft (https://jahasoft.pk)
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">View detailed business reports and analytics.</p>
        </div>
        <button
          onClick={handleOpenExport}
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-98 transition-all self-start sm:self-auto"
          title="Export / Print Current Report as PDF"
        >
          <Download className="h-4 w-4" />
          <span>Export PDF / Print</span>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
              activeTab === tab.id
                ? 'bg-emerald-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== 'stock' && (
        <DateFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onApply={handleApplyDates}
          onReset={resetDate}
        />
      )}

      {/* DAILY REPORT */}
      {activeTab === 'daily' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Sales</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">Rs. {totalSales.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100"><ShoppingCart className="h-5 w-5 text-blue-600" /></div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{salesCount} transactions</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Purchases</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">Rs. {totalPurchases.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100"><Truck className="h-5 w-5 text-purple-600" /></div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{purchasesCount} orders</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">Rs. {totalExpenses.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100"><Receipt className="h-5 w-5 text-red-600" /></div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{expensesCount} entries</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Net Profit</p>
                  <p className={clsx('text-2xl font-bold mt-1', profit >= 0 ? 'text-emerald-600' : 'text-red-600')}>Rs. {profit.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className={clsx('flex h-10 w-10 items-center justify-center rounded-lg', profit >= 0 ? 'bg-emerald-100' : 'bg-red-100')}>
                  {profit >= 0 ? <TrendingUp className="h-5 w-5 text-emerald-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{netMargin}% margin</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Clock className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">Recent Sales</h2>
              </div>
              <div className="p-6">
                {sales.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {sales.slice(0, 10).map((sale) => (
                      <div key={sale.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                        <div>
                          <p className="font-mono text-sm font-semibold text-gray-900">{sale.invoice_number}</p>
                          <p className="text-xs text-gray-400">{formatPakistanDateTime(sale.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">Rs. {(parseFloat(sale.total) || 0).toFixed(2)}</p>
                          <p className={clsx('text-xs font-semibold capitalize', sale.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600')}>{sale.payment_status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-400 py-8">No sales in selected period</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Receipt className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">Expenses Breakdown</h2>
              </div>
              <div className="p-6">
                {expensesCategories.length > 0 ? (
                  <div className="space-y-3">
                    {expensesCategories.map((cat, i) => (
                      <div key={cat.category || i} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{cat.category}</span>
                        <span className="text-sm font-bold text-red-600">Rs. {(parseFloat(cat.total) || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-400 py-8">No expenses in selected period</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PROFIT REPORT */}
      {activeTab === 'profit' && (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 shadow-sm text-white">
            <h3 className="text-lg font-semibold">Profit & Loss Summary</h3>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className="text-emerald-100 text-xs font-medium">Revenue</p><p className="text-xl font-bold">Rs. {totalSales.toFixed(2)}</p></div>
              <div><p className="text-emerald-100 text-xs font-medium">Cost of Goods</p><p className="text-xl font-bold">Rs. {cogs.toFixed(2)}</p></div>
              <div><p className="text-emerald-100 text-xs font-medium">Operating Expenses</p><p className="text-xl font-bold">Rs. {totalExpenses.toFixed(2)}</p></div>
              <div><p className="text-emerald-100 text-xs font-medium">Net Profit</p><p className={clsx('text-xl font-bold', profit >= 0 ? 'text-white' : 'text-red-200')}>Rs. {profit.toFixed(2)}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gross Profit</p>
              <p className={clsx('text-2xl font-bold mt-1', grossProfit >= 0 ? 'text-emerald-600' : 'text-red-600')}>Rs. {grossProfit.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">Revenue - Cost of Goods</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gross Margin</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{grossMargin}%</p>
              <p className="mt-1 text-xs text-gray-400">Gross Profit / Revenue</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Net Margin</p>
              <p className={clsx('text-2xl font-bold mt-1', profit >= 0 ? 'text-emerald-600' : 'text-red-600')}>{netMargin}%</p>
              <p className="mt-1 text-xs text-gray-400">Net Profit / Revenue</p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Wallet className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Revenue Sources</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg bg-emerald-50 p-4">
                  <div><p className="text-sm font-semibold text-gray-900">Cash Sales (Paid)</p><p className="text-xs text-gray-500">{financialData?.sales?.paidCount || 0} transactions</p></div>
                  <p className="text-lg font-bold text-emerald-600">Rs. {paidSales.toFixed(2)}</p>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-amber-50 p-4">
                  <div><p className="text-sm font-semibold text-gray-900">Credit Sales (Unpaid/Partial)</p><p className="text-xs text-gray-500">{financialData?.sales?.creditCount || 0} transactions</p></div>
                  <p className="text-lg font-bold text-amber-600">Rs. {creditSales.toFixed(2)}</p>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-red-50 p-4">
                  <div><p className="text-sm font-semibold text-gray-900">Total Expenses</p><p className="text-xs text-gray-500">{expensesCount} entries</p></div>
                  <p className="text-lg font-bold text-red-600">Rs. {totalExpenses.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STOCK REPORT */}
      {activeTab === 'stock' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Total Stock Value</p>
              <p className="text-2xl font-extrabold text-emerald-700 mt-1">
                Rs. {stockSummary.stockValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-xs font-semibold text-emerald-600">
                {stockSummary.inStockItems} items in stock (at cost price)
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Retail Value</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                Rs. {stockSummary.retailValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Potential Profit: Rs. {stockSummary.potentialProfit.toLocaleString('en-PK', { minimumFractionDigits: 2 })} ({stockSummary.margin}%)
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Low Stock Items</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stockSummary.lowStockCount}</p>
              <p className="mt-1 text-xs text-gray-400">At or below reorder level</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stockSummary.outOfStockCount}</p>
              <p className="mt-1 text-xs text-gray-400">Zero quantity items (of {stockSummary.totalMedicines} active)</p>
            </div>
          </div>

          {/* Table Card */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">Inventory Items</h2>
                <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                  {stockPagination.total} items
                </span>
              </div>

              {/* Status Filters and Search */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex rounded-lg bg-gray-100 p-1 text-xs font-semibold">
                  <button
                    onClick={() => handleStockStatusChange('in_stock')}
                    className={clsx(
                      'rounded-md px-3 py-1.5 transition-all',
                      stockStatus === 'in_stock' ? 'bg-white text-emerald-700 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    In Stock ({stockSummary.inStockItems})
                  </button>
                  <button
                    onClick={() => handleStockStatusChange('low_stock')}
                    className={clsx(
                      'rounded-md px-3 py-1.5 transition-all',
                      stockStatus === 'low_stock' ? 'bg-white text-amber-700 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    Low Stock ({stockSummary.lowStockCount})
                  </button>
                  <button
                    onClick={() => handleStockStatusChange('out_of_stock')}
                    className={clsx(
                      'rounded-md px-3 py-1.5 transition-all',
                      stockStatus === 'out_of_stock' ? 'bg-white text-red-700 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    Out of Stock ({stockSummary.outOfStockCount})
                  </button>
                  <button
                    onClick={() => handleStockStatusChange('all')}
                    className={clsx(
                      'rounded-md px-3 py-1.5 transition-all',
                      stockStatus === 'all' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    All ({stockSummary.totalMedicines})
                  </button>
                </div>

                <form onSubmit={handleStockSearch} className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={stockSearch}
                      onChange={(e) => setStockSearch(e.target.value)}
                      placeholder="Search name, generic..."
                      className="w-48 sm:w-60 rounded-lg border border-gray-200 pl-9 pr-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-hidden focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Filter
                  </button>
                </form>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Medicine</th>
                    <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Stock</th>
                    <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Reorder</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Cost Price</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Selling Price</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Stock Value (Cost)</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Retail Value</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stockItems.map((m) => (
                    <tr key={m.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                        <div>{m.name}</div>
                        {m.generic_name && <div className="text-[11px] text-gray-400 font-normal">{m.generic_name}</div>}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const stockInfo = formatItemQuantityDisplay(m.total_stock, m.units);
                          return (
                            <div>
                              <span className="font-bold text-gray-800 text-sm">{stockInfo.primary}</span>
                              {stockInfo.breakdown && (
                                <div className="text-[10px] text-gray-500 font-medium mt-0.5">{stockInfo.breakdown}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-center text-sm text-gray-400">
                        {m.reorder_level || 0} {m.base_unit || m.unit || ''}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600">Rs. {(parseFloat(m.cost_price) || 0).toFixed(2)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600">Rs. {(parseFloat(m.selling_price) || 0).toFixed(2)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-bold text-emerald-700">Rs. {(parseFloat(m.cost_value) || 0).toFixed(2)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-blue-700">Rs. {(parseFloat(m.retail_value) || 0).toFixed(2)}</td>
                      <td className="sticky-col-right px-6 py-3 whitespace-nowrap text-center">
                        <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          m.status === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                            m.status === 'low_stock' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                        )}>
                          {m.status === 'out_of_stock' ? 'Out of Stock' : m.status === 'low_stock' ? 'Low Stock' : 'In Stock'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {stockItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Package className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm">No medicines found matching criteria</p>
              </div>
            )}

            {/* Pagination footer */}
            {stockPagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3 bg-gray-50/50">
                <p className="text-xs text-gray-500">
                  Showing <span className="font-semibold">{((stockPagination.page - 1) * stockPagination.limit) + 1}</span> to{' '}
                  <span className="font-semibold">{Math.min(stockPagination.page * stockPagination.limit, stockPagination.total)}</span> of{' '}
                  <span className="font-semibold">{stockPagination.total}</span> items
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleStockPageChange(stockPagination.page - 1)}
                    disabled={stockPagination.page <= 1}
                    className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    <span>Previous</span>
                  </button>
                  <span className="text-xs text-gray-600 px-2">
                    Page {stockPagination.page} of {stockPagination.totalPages}
                  </span>
                  <button
                    onClick={() => handleStockPageChange(stockPagination.page + 1)}
                    disabled={stockPagination.page >= stockPagination.totalPages}
                    className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SALES REPORT */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Sales</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">Rs. {totalSales.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">{salesCount} invoices</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Paid Sales</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">Rs. {paidSales.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">{financialData?.sales?.paidCount || 0} invoices</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Credit Sales</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">Rs. {creditSales.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">{financialData?.sales?.creditCount || 0} invoices</p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <ShoppingCart className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Sales Transactions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Invoice</th>
                    <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Total</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Paid</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sales.map((sale) => (
                    <tr key={sale.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-3 whitespace-nowrap font-mono text-sm font-semibold text-gray-900">{sale.invoice_number}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">{formatPakistanDate(sale.created_at)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-bold text-gray-900">Rs. {(parseFloat(sale.total) || 0).toFixed(2)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600">Rs. {(parseFloat(sale.paid_amount) || 0).toFixed(2)}</td>
                      <td className="sticky-col-right px-6 py-3 whitespace-nowrap text-center">
                        <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                          sale.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        )}>{sale.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sales.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <ShoppingCart className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm">No sales in selected period</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PURCHASE REPORT */}
      {activeTab === 'purchases' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Purchases</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">Rs. {totalPurchases.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">{purchasesCount} orders</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Suppliers</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{suppliersCount}</p>
              <p className="mt-1 text-xs text-gray-400">Active suppliers</p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Truck className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Purchase Orders</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reference</th>
                    <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Total</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {purchases.map((p) => (
                    <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-3 whitespace-nowrap font-mono text-sm font-semibold text-gray-900">{p.reference_number}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">{new Date(p.purchase_date || p.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-bold text-gray-900">Rs. {(parseFloat(p.total) || 0).toFixed(2)}</td>
                      <td className="sticky-col-right px-6 py-3 whitespace-nowrap text-center">
                        <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                          p.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        )}>{p.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {purchases.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Truck className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm">No purchases in selected period</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EXPENSE REPORT */}
      {activeTab === 'expenses' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600 mt-1">Rs. {totalExpenses.toFixed(2)}</p>
              <p className="mt-1 text-xs text-gray-400">{expensesCount} entries</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Categories</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{expensesCategories.length}</p>
              <p className="mt-1 text-xs text-gray-400">Expense types</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <BarChart3 className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">By Category</h2>
              </div>
              <div className="p-6">
                {expensesCategories.length > 0 ? (
                  <div className="space-y-3">
                    {expensesCategories.map((cat, i) => (
                      <div key={cat.category || i} className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                        <span className="text-sm font-medium text-gray-700">{cat.category}</span>
                        <span className="text-sm font-bold text-red-600">Rs. {(parseFloat(cat.total) || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-400 py-8">No expenses in selected period</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Receipt className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">All Expenses</h2>
              </div>
              <div className="p-6">
                {expenses.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {expenses.map((e) => (
                      <div key={e.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{e.category}</p>
                          <p className="text-xs text-gray-400">{e.date} {e.description ? `- ${e.description}` : ''}</p>
                        </div>
                        <p className="text-sm font-bold text-red-600">Rs. {(parseFloat(e.amount) || 0).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-400 py-8">No expenses in selected period</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ITEM SALES REPORT */}
      {activeTab === 'item_sales' && (
        <div className="space-y-6">
          {/* Summary Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Units Sold</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">
                    {(soldItemsSummary.totalSoldQty || 0).toLocaleString()}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                  <PackageCheck className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{soldItemsSummary.totalTransactions || 0} customer sales</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Sales Value</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">
                    Rs. {(soldItemsSummary.totalSoldAmount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">Sold items revenue</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Distinct Products</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">
                    {soldItemsSummary.distinctItemsCount || 0}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <Boxes className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">Unique medicines sold</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Estimated Profit</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">
                    Rs. {(soldItemsSummary.totalProfit || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">{soldItemsSummary.margin || 0}% margin</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <form onSubmit={handleSoldItemsSearchSubmit} className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sold items by medicine name, generic name, barcode, sku..."
                  value={soldItemsSearch}
                  onChange={(e) => setSoldItemsSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:border-emerald-500 focus:bg-white focus:outline-none transition-all"
                />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="submit"
                  className="w-full sm:w-auto rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors shadow-xs"
                >
                  Search
                </button>
                {soldItemsSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setSoldItemsSearch('');
                      setSoldItemsPage(1);
                      loadSoldItemsData(1, '', dateFrom, dateTo);
                    }}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Items Table */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <PackageCheck className="h-5 w-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-900">Sold Items & Stock Movement</h2>
              </div>
              <span className="text-xs text-gray-500 font-medium">
                {soldItemsPagination.total > 0
                  ? `Showing ${(soldItemsPagination.page - 1) * soldItemsPagination.limit + 1}–${Math.min(
                    soldItemsPagination.page * soldItemsPagination.limit,
                    soldItemsPagination.total
                  )} of ${soldItemsPagination.total} items (8 per page)`
                  : '0 items'}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">#</th>
                    <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Medicine Name</th>
                    <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Category</th>
                    <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Sold Qty</th>
                    <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Remaining Stock</th>
                    <th className="px-6 py-3 whitespace-nowrap text-center text-[10px] font-bold uppercase tracking-wider text-gray-500">Initial / Total Qty</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Avg Unit Price</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Total Sales</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {soldItemsList.map((item, index) => (
                    <tr key={item.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-3 whitespace-nowrap text-xs text-gray-400">
                        {(soldItemsPagination.page - 1) * soldItemsPagination.limit + index + 1}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="font-semibold text-gray-900 text-sm">{item.name}</div>
                        {item.generic_name && (
                          <div className="text-[11px] text-gray-400 font-normal">{item.generic_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          {item.category_name}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const qtyInfo = formatItemQuantityDisplay(item.sold_qty, item.units);
                          return (
                            <div>
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">
                                {qtyInfo.primary}
                              </span>
                              {qtyInfo.breakdown && (
                                <div className="text-[10px] text-emerald-700 font-medium mt-0.5">{qtyInfo.breakdown}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const stockInfo = formatItemQuantityDisplay(item.remaining_stock, item.units);
                          return (
                            <div>
                              <span className={clsx(
                                'inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-bold',
                                item.stock_status === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                                  item.stock_status === 'low_stock' ? 'bg-amber-100 text-amber-700' :
                                    'bg-gray-100 text-gray-800'
                              )}>
                                {stockInfo.primary}
                              </span>
                              {stockInfo.breakdown && (
                                <div className="text-[10px] text-gray-500 mt-0.5">{stockInfo.breakdown}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const initInfo = formatItemQuantityDisplay(item.initial_stock, item.units);
                          return (
                            <div>
                              <div className="font-semibold text-gray-800 text-sm">{initInfo.primary}</div>
                              {initInfo.breakdown && (
                                <div className="text-[10px] text-gray-600 font-medium">{initInfo.breakdown}</div>
                              )}
                              <div className="text-[10px] text-gray-400">({item.sold_qty} sold + {item.remaining_stock} left)</div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600">
                        <div>Rs. {(parseFloat(item.avg_price) || 0).toFixed(2)}</div>
                        <div className="text-[10px] text-gray-400">/ {item.base_unit || item.unit || 'unit'}</div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-bold text-gray-900">
                        Rs. {(parseFloat(item.total_amount) || 0).toFixed(2)}
                      </td>
                      <td className="sticky-col-right px-6 py-3 whitespace-nowrap text-right">
                        <div className="font-bold text-emerald-600 text-sm">
                          Rs. {(parseFloat(item.profit) || 0).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-gray-400">{item.margin}% margin</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {soldItemsList.length === 0 && !soldItemsLoading && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <PackageCheck className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm font-medium">No sold items in selected period</p>
                <p className="text-xs text-gray-400 mt-1">Try selecting a different date range or clear search</p>
              </div>
            )}

            {/* Pagination Controls (8 items per page) */}
            {soldItemsPagination.totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5">
                <button
                  type="button"
                  disabled={soldItemsPagination.page <= 1}
                  onClick={() => {
                    const newPage = soldItemsPagination.page - 1;
                    setSoldItemsPage(newPage);
                    loadSoldItemsData(newPage, soldItemsSearch, dateFrom, dateTo);
                  }}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs transition-all"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: soldItemsPagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => {
                        setSoldItemsPage(pageNum);
                        loadSoldItemsData(pageNum, soldItemsSearch, dateFrom, dateTo);
                      }}
                      className={clsx(
                        'min-w-[32px] h-8 rounded-lg text-xs font-semibold transition-all',
                        soldItemsPagination.page === pageNum
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={soldItemsPagination.page >= soldItemsPagination.totalPages}
                  onClick={() => {
                    const newPage = soldItemsPagination.page + 1;
                    setSoldItemsPage(newPage);
                    loadSoldItemsData(newPage, soldItemsSearch, dateFrom, dateTo);
                  }}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs transition-all"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* REPORTS PDF EXPORT PREVIEW MODAL */}
      {showExportPDF &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <div className="fixed inset-0" onClick={() => setShowExportPDF(false)} />
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Download className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{getTabLabel(activeTab)} Preview</h3>
                    <p className="text-[10px] text-gray-500">Generated {formatPakistanDateTime(new Date())}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-gray-100 flex justify-center">
                <div id="printable-reports-modal-view" className="w-full max-w-[750px] bg-white shadow-md border border-gray-200 rounded-lg p-2 sm:p-6">
                  {renderPrintableReport()}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white p-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-emerald-600/25 hover:bg-emerald-700 active:scale-98 transition-all"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print / Save as PDF</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DEDICATED PRINT CONTAINER FOR REPORTS EXPORT */}
      {showExportPDF &&
        createPortal(
          <div id="printable-reports-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 20, color: '#1a1a1a' }}>
            {renderPrintableReport()}
          </div>,
          document.body
        )}
    </div>
  );
}
